import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { supabase } from "./supabaseClient";
import Nav from "./Nav";
import { LazyImage } from "./FleetPage";

// Star Citizen Items catalog. Reads from `sc_items`, which is also where blueprints
// live (migrated in via the sc_items_catalog migration). Deep link: /resources/items/:itemId.

const ACCENT = "#4fc3f7";

function StarField() {
  const canvasRef = React.useRef(null);
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    const stars = Array.from({ length: 200 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.5 + 0.3,
      a: Math.random(), speed: Math.random() * 0.003 + 0.001,
    }));
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach(s => {
        s.a += s.speed;
        if (s.a > 1) s.a = 0;
        const alpha = Math.sin(s.a * Math.PI) * 0.7 + 0.1;
        ctx.beginPath();
        ctx.arc(s.x * canvas.width, s.y * canvas.height, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${alpha})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100vh", pointerEvents: "none", zIndex: 0 }} />;
}

function SectionLabel({ children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
      <div style={{ width: 24, height: 2, background: ACCENT }} />
      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 4, color: ACCENT, textTransform: "uppercase" }}>{children}</span>
    </div>
  );
}

/* ─── Item Detail (deep-link target) ──────────────────────────────────────── */
function ItemDetail({ itemId, onBack, allItems }) {
  const item = useMemo(() => (allItems || []).find(i => i.id === itemId) || null, [allItems, itemId]);

  if (!item) {
    return (
      <div style={{ textAlign: "center", padding: "120px 0" }}>
        <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, letterSpacing: 3, color: "rgba(255,255,255,0.2)", marginBottom: 20 }}>ITEM NOT FOUND</div>
        <button onClick={onBack} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", padding: "8px 20px", border: `1px solid ${ACCENT}`, color: ACCENT, background: "transparent", cursor: "pointer" }}>
          Back to Items
        </button>
      </div>
    );
  }

  return (
    <div>
      <button onClick={onBack} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", padding: "8px 20px", border: `1px solid ${ACCENT}55`, color: "rgba(232,234,246,0.55)", background: "transparent", cursor: "pointer", marginBottom: 32, transition: "all 0.2s" }}
        onMouseEnter={e => { e.currentTarget.style.color = ACCENT; e.currentTarget.style.borderColor = ACCENT; }}
        onMouseLeave={e => { e.currentTarget.style.color = "rgba(232,234,246,0.55)"; e.currentTarget.style.borderColor = `${ACCENT}55`; }}>
        &larr; Back to Items
      </button>

      <div style={{ display: "flex", gap: 40, flexWrap: "wrap", marginBottom: 48 }}>
        <div style={{ width: "100%", maxWidth: 480, flexShrink: 0 }}>
          <div style={{ width: "100%", aspectRatio: "1/1", background: "rgba(255,255,255,0.03)", border: `1px solid ${ACCENT}25`, overflow: "hidden", position: "relative" }}>
            {item.image_url ? (
              <LazyImage src={item.image_url} alt={item.name} thumbnailWidth={960} style={{ width: "100%", height: "100%" }} />
            ) : (
              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 56, color: `${ACCENT}1a` }}>
                ◇
              </div>
            )}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: ACCENT, textTransform: "uppercase", padding: "3px 10px", border: `1px solid ${ACCENT}55`, background: `${ACCENT}10` }}>
              {item.category}
            </span>
            {item.subcategory && (
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.55)", textTransform: "uppercase", padding: "3px 10px", border: "1px solid rgba(255,255,255,0.12)" }}>
                {item.subcategory}
              </span>
            )}
            {item.manufacturer && (
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.55)", textTransform: "uppercase", padding: "3px 10px", border: "1px solid rgba(255,255,255,0.12)" }}>
                {item.manufacturer}
              </span>
            )}
          </div>
          <h1 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "clamp(22px, 3.5vw, 38px)", color: "#e8eaf6", marginBottom: 18 }}>
            {item.name}
          </h1>
          {item.description && (
            <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 16, color: "rgba(232,234,246,0.65)", lineHeight: 1.7, marginBottom: 20 }}>
              {item.description}
            </p>
          )}
          {item.source_url && (
            <a href={item.source_url} target="_blank" rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", padding: "10px 20px", border: `1px solid ${ACCENT}`, color: ACCENT, textDecoration: "none", transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.background = ACCENT; e.currentTarget.style.color = "#05070f"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = ACCENT; }}>
              View on Wiki &rarr;
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Items Grid ──────────────────────────────────────────────────────────── */
function ItemsGrid({ items, onSelect }) {
  const [search, setSearch] = useState("");
  const [filterGroup, setFilterGroup] = useState("");
  const [filterCategory, setFilterCategory] = useState("");

  // Top-level groups (Components, Weapons, Armor, …)
  const groups = useMemo(() => {
    return [...new Set(items.map(i => i.item_group).filter(Boolean))].sort();
  }, [items]);

  // Categories — narrowed to the selected group when one is active so the
  // category dropdown doesn't show options that would yield zero results.
  const categories = useMemo(() => {
    const pool = filterGroup ? items.filter(i => i.item_group === filterGroup) : items;
    const cats = new Set();
    pool.forEach(i => {
      if (i.category) cats.add(i.category);
      if (Array.isArray(i.tags)) i.tags.forEach(t => cats.add(t));
    });
    return [...cats].sort();
  }, [items, filterGroup]);

  // If the active category isn't in the selected group's category list, drop it.
  useEffect(() => {
    if (filterCategory && !categories.includes(filterCategory)) setFilterCategory("");
  }, [categories, filterCategory]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(i => {
      if (filterGroup && i.item_group !== filterGroup) return false;
      if (filterCategory && i.category !== filterCategory && !(Array.isArray(i.tags) && i.tags.includes(filterCategory))) return false;
      if (q) {
        const hay = `${i.name || ""} ${i.subcategory || ""} ${i.manufacturer || ""} ${i.category || ""} ${i.item_group || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, filterGroup, filterCategory]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [filtered]);

  // Group displayed results by category for visual hierarchy when no narrow filter is active.
  // (We still group when only filterGroup is set, since that just narrows the universe of categories.)
  const grouped = useMemo(() => {
    if (filterCategory || search) return null;
    const map = {};
    sorted.forEach(i => {
      const k = i.category || "Other";
      if (!map[k]) map[k] = [];
      map[k].push(i);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [sorted, filterCategory, search]);

  function renderCard(item) {
    return (
      <div key={item.id} onClick={() => onSelect(item.id)} style={{
        border: `1px solid ${ACCENT}25`, background: "rgba(255,255,255,0.02)",
        cursor: "pointer", transition: "all 0.25s", overflow: "hidden",
        clipPath: "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 0 100%)",
        position: "relative",
      }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = `${ACCENT}88`; e.currentTarget.style.background = `${ACCENT}0a`; e.currentTarget.style.transform = "translateY(-2px)"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = `${ACCENT}25`; e.currentTarget.style.background = "rgba(255,255,255,0.02)"; e.currentTarget.style.transform = "translateY(0)"; }}>
        <div style={{ position: "relative", width: "100%", aspectRatio: "1/1", background: "rgba(0,0,0,0.3)", overflow: "hidden" }}>
          {item.image_url ? (
            <LazyImage src={item.image_url} alt={item.name} thumbnailWidth={400} style={{ width: "100%", height: "100%" }} />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 36, color: `${ACCENT}18` }}>
              ◇
            </div>
          )}
          {item.subcategory && (
            <div className="item-badge" style={{ position: "absolute", top: 6, left: 6, background: "rgba(5,7,15,0.85)", backdropFilter: "blur(8px)", padding: "3px 8px", border: "1px solid rgba(255,255,255,0.12)" }}>
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, color: "rgba(232,234,246,0.7)", textTransform: "uppercase" }}>{item.subcategory}</span>
            </div>
          )}
        </div>
        <div className="item-card-content" style={{ padding: "12px 14px" }}>
          <div className="item-name" style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 13, color: "#e8eaf6", marginBottom: 4, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{item.name}</div>
          <div className="item-meta" style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: "rgba(232,234,246,0.4)", textTransform: "uppercase", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {item.manufacturer || item.category}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <SectionLabel>Star Citizen Items</SectionLabel>
        <h1 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "clamp(28px, 4vw, 48px)", color: "#e8eaf6", marginBottom: 8 }}>
          Item Catalog
        </h1>
        <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 17, color: "rgba(232,234,246,0.45)" }}>
          {items.length.toLocaleString()} items across {categories.length} categories. Every item has a sharable link.
        </p>
      </div>

      {/* Search + group + category filter — group is the top-level bucket
          (Components, Weapons, Armor, …); category is the per-type subdivision. */}
      <div style={{ display: "flex", gap: 12, marginBottom: 28, flexWrap: "wrap" }}>
        <input type="text" placeholder="Search items..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 200, padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}25`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2 }} />
        <select value={filterGroup} onChange={e => setFilterGroup(e.target.value)}
          style={{ padding: "10px 14px", background: "#0a0610", border: `1px solid ${ACCENT}25`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2, minWidth: 170 }}>
          <option value="">All Groups</option>
          {groups.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          style={{ padding: "10px 14px", background: "#0a0610", border: `1px solid ${ACCENT}25`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2, minWidth: 200 }}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {sorted.length === 0 ? (
        <div style={{ textAlign: "center", padding: "80px 0", fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>
          {search || filterCategory ? "NO ITEMS MATCH YOUR FILTERS" : "NO ITEMS IN THE CATALOG"}
        </div>
      ) : grouped ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 36 }}>
          {grouped.map(([cat, list]) => (
            <div key={cat}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 12 }}>
                <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 16, color: ACCENT, letterSpacing: 1 }}>{cat}</h2>
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)" }}>{list.length}</span>
                <div style={{ flex: 1, height: 1, background: `${ACCENT}15` }} />
              </div>
              <div className="items-grid">
                {list.map(renderCard)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="items-grid">
          {sorted.map(renderCard)}
        </div>
      )}
    </div>
  );
}

/* ─── Main page ──────────────────────────────────────────────────────────── */
export default function ItemsPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(itemId || null);

  useEffect(() => { setSelected(itemId || null); }, [itemId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Supabase caps a single .select() at 1000 rows by default — well below our
      // ~4-5k sc_items count. Page through with .range() until we get a short page.
      const pageSize = 1000;
      const out = [];
      for (let from = 0; ; from += pageSize) {
        const { data, error } = await supabase
          .from("sc_items")
          .select("id, name, slug, item_group, category, subcategory, manufacturer, description, image_url, source_url, archived, tags")
          .eq("archived", false)
          .order("name")
          .range(from, from + pageSize - 1);
        if (error) { console.error("[items] page load failed", error); break; }
        if (!data || data.length === 0) break;
        out.push(...data);
        if (data.length < pageSize) break;
      }
      if (cancelled) return;
      setItems(out);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Preserve grid scroll across a card-click → detail → Back round trip.
  const SCROLL_KEY = "itemsGridScrollY";
  const handleSelect = useCallback((id) => {
    try { sessionStorage.setItem(SCROLL_KEY, String(window.scrollY)); } catch (e) {}
    setSelected(id);
    navigate(`/resources/items/${id}`);
  }, [navigate]);

  // Use in-app history when available so a back from an item detail returns
  // to the referring page; deep-linked visits fall back to the grid.
  const handleBack = useCallback(() => {
    setSelected(null);
    const idx = window.history.state?.idx;
    if (typeof idx === "number" && idx > 0) navigate(-1);
    else navigate("/resources/items");
  }, [navigate]);

  // Restore the saved scroll when returning to the grid view. rAF waits for
  // render, the 200ms timeout catches LazyImage-induced layout shifts.
  useEffect(() => {
    if (selected != null) return;
    let saved;
    try { saved = sessionStorage.getItem(SCROLL_KEY); } catch (e) { return; }
    if (saved == null) return;
    const y = parseInt(saved, 10);
    if (!Number.isFinite(y)) return;
    try { sessionStorage.removeItem(SCROLL_KEY); } catch (e) {}
    const raf = requestAnimationFrame(() => window.scrollTo(0, y));
    const t = setTimeout(() => window.scrollTo(0, y), 200);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [selected]);

  if (loading) {
    return (
      <div style={{ background: "#05070f", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 13, letterSpacing: 3, color: `${ACCENT}99` }}>
        LOADING…
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Rajdhani', 'Orbitron', sans-serif", background: "#05070f", color: "#e8eaf6", minHeight: "100vh", overflowX: "hidden" }}>
      <StarField />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&family=Orbitron:wght@400;600;700;900&family=Space+Mono&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::selection { background: ${ACCENT}; color: #05070f; }
        a { text-decoration: none; color: inherit; }
        .nav-link { font-family: 'Orbitron', sans-serif; font-size: 11px; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(232,234,246,0.55); transition: color 0.2s; padding: 4px 0; position: relative; }
        .nav-link::after { content: ''; position: absolute; bottom: 0; left: 0; width: 0; height: 1px; background: ${ACCENT}; transition: width 0.3s ease; }
        .nav-link:hover { color: ${ACCENT}; }
        .nav-link:hover::after { width: 100%; }
        .scanline { position: fixed; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 9999; background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px); }
        input, select, textarea { color-scheme: dark; }
        input:focus, select:focus, textarea:focus { outline: none; border-color: ${ACCENT} !important; }
        .items-grid { display: grid; gap: 16px; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); }
        @media (max-width: 700px) {
          .items-grid { gap: 8px; grid-template-columns: repeat(auto-fill, minmax(108px, 1fr)); }
          .item-card-content { padding: 8px 9px !important; }
          .item-name { font-size: 11px !important; line-height: 1.25 !important; }
          .item-meta { font-size: 8px !important; }
          .item-badge { font-size: 7px !important; padding: 2px 5px !important; top: 5px !important; left: 5px !important; }
        }
      `}</style>
      <div className="scanline" />
      <Nav accentColor={ACCENT} />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 1200, margin: "0 auto", padding: "112px clamp(16px, 4vw, 40px) 48px" }}>
        <div style={{ marginBottom: 18 }}>
          <Link to="/resources" style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.45)", textTransform: "uppercase" }}>
            &larr; Resources
          </Link>
        </div>

        {selected ? (
          <ItemDetail itemId={selected} onBack={handleBack} allItems={items} />
        ) : (
          <ItemsGrid items={items} onSelect={handleSelect} />
        )}
      </div>

      <footer style={{ position: "relative", zIndex: 1, borderTop: "1px solid rgba(255,255,255,0.07)", padding: "32px 40px", background: "rgba(0,0,0,0.4)", marginTop: 60 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.2, color: "rgba(232,234,246,0.18)", lineHeight: 1.8 }}>
          &copy; 2026 Starkeeper Industries. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
